/**
 * How this theme ships CSS.
 *
 * <p>The storefront's Tailwind build only scans its own sources, so utility
 * classes written here would never be generated. The platform blocks solve the
 * same problem with inline styles plus a scoped `<style>` element. This theme
 * does the same, using React 19's hoistable `<style href precedence>`, so each
 * stylesheet is emitted ONCE per document however many blocks pull it in, and
 * lands in `<head>`.
 *
 * <p>Every rule is scoped under an `ae-` class — or, for the chrome bridge
 * below, under a `:has()` selector that matches only the shell elements
 * carrying this theme's own chrome — so nothing leaks onto the platform's
 * blocks on any other shop.
 */
export function TradeStyles({ id, css }: { id: string; css: string }) {
  return (
    <style href={`solar-pv-${id}`} precedence="solar-pv">
      {css}
    </style>
  );
}

/**
 * Base layer: the scope variables every skinned block reads, plus the chrome
 * bridge that turns the shell's header into Horizon's dark-ink brand voice.
 *
 * <p>Each `--ae-*` variable reads the tenant's design token first and falls back
 * to a literal, so a route whose payload carries no tokens still renders as
 * intended rather than collapsing to browser defaults.
 *
 * <p>The literals are the "Horizon" palette the owner approved for the
 * 2026-10-01 redesign (`solar/docs/design/redesign-2026-10-01/css/tokens.css`):
 * near-black navy ink for the chrome voice, paper and white selling surfaces,
 * sea-blue links, and one dawn-amber accent whose luminous form `#FFB547` is
 * graphic-only on light surfaces (its text form is `#8A5600`). They are
 * duplicated here as fallbacks only — the tokens in `solar-pv.json` (and on the
 * live shop, `allow-energy-palette.json`) are the source of truth, and
 * `build-palette.py --write` is what changes them. Recolouring the shop never
 * needs this file. The light mood ("Horizon Day", `css/skin-light.css`) arrives
 * the same way: a palette merge flips the tokens and everything here follows.
 */
export const BASE_CSS = `
/* A snapshot of the selling-surface tokens, taken where nothing has remapped
   them. The chrome bridge below remaps the raw \`--color-*\` names on the
   shell's own header, because its inline styles read those names and inline
   values are otherwise unreachable from a stylesheet; anything inside that
   scope that must render as a light panel again (the search suggestions) is
   restored from this snapshot. The theme engine writes the tenant tokens on
   \`:root\` (\`themeToCss\`), so these compute from the true theme values. The
   names are custom properties only — no visual rule hangs off \`:root\`. */
:root{
  --ae-page-surface:var(--color-surface-card,#ffffff);
  --ae-page-raised:var(--color-surface-muted,#f6f8fb);
  --ae-page-ink:var(--color-text-primary,#182435);
  --ae-page-muted:var(--color-text-secondary,#51607a);
  --ae-page-hover:var(--color-neutral-100,#f6f8fb);
  --ae-page-hairline:var(--color-neutral-200,#e3e9f2);
  --ae-page-line:var(--color-border-subtle,#e3e9f2);
  --ae-page-line-strong:var(--color-border-strong,#c9d4e3);
  --ae-page-focus:var(--color-border-focus,#2a5680);
  --ae-page-brand:var(--color-brand-500,#2a5680);
  --ae-page-brand-deep:var(--color-brand-600,#1e4066);
  --ae-page-on-brand:var(--color-text-on-brand,#ffffff);
}

.ae{
  --ae-ink:var(--color-text-primary,#182435);
  --ae-muted:var(--color-text-secondary,#51607a);
  --ae-brand:var(--color-brand-500,#2a5680);
  --ae-brand-deep:var(--color-brand-600,#1e4066);
  --ae-brand-text:var(--color-text-brand,#2a5680);
  --ae-link:var(--color-text-link,#2a5680);
  --ae-link-hover:var(--color-text-link-hover,#1e4066);
  --ae-on-brand:var(--color-text-on-brand,#ffffff);
  --ae-tint:var(--color-brand-50,#f6f8fb);
  --ae-accent:var(--color-accent-500,#ffb547);
  --ae-accent-deep:var(--color-accent-600,#f59e2e);
  --ae-accent-tint:var(--color-accent-100,rgba(255,181,71,.18));
  --ae-accent-line:var(--color-accent-200,rgba(255,181,71,.18));
  --ae-accent-ink:var(--color-accent-800,#8a5600);
  --ae-on-accent:var(--color-text-on-accent,#10233e);
  --ae-surface:var(--color-surface-card,#ffffff);
  --ae-subtle:var(--color-surface-muted,#f6f8fb);
  --ae-border:var(--color-border-default,#e3e9f2);
  --ae-border-strong:var(--color-border-strong,#c9d4e3);
  --ae-focus:var(--color-border-focus,#2a5680);
  --ae-success:var(--color-text-success,#1d7a4f);
  --ae-error:var(--color-text-error,#b3261e);

  /* Calm, not bubbly: inputs and chips sit at 8px, cards at 12px — the
     mockups' --r-s and --r-m — read from the same tokens the platform's own
     blocks use so both sides of a page agree. Buttons (--r-btn, 10px) read
     \`--radius-md\` directly where they are drawn. */
  --ae-radius:var(--radius-button,8px);
  --ae-radius-lg:var(--radius-card,12px);
  --ae-shadow:var(--shadow-md,0 4px 12px rgba(10,20,36,.07),0 16px 40px rgba(10,20,36,.09));
  --ae-shadow-sm:var(--shadow-sm,0 1px 2px rgba(10,20,36,.05),0 1px 3px rgba(10,20,36,.07));
  --ae-gutter:clamp(16px,2.5vw,24px);
  --ae-max:var(--app-max-width,1320px);
}

/* ── The chrome scope: Horizon's dark-ink brand voice ───────────────────────
 * The header, the department band and the trade VAT strip paint as one piece
 * of near-black navy chrome ("dark ink is the brand voice, light paper is the
 * selling surface"). These variables read the palette's band tokens first —
 * the role leaves \`build-palette.py\` emits (\`color.band.chrome\` for the
 * header bar, \`.raised\` for fields, \`.hover\`, with the on-band text and
 * hairlines as \`--color-text-inverse(-muted)\` / \`--color-border-inverse(-strong)\`
 * and amber type as \`--color-accent-on-band\`), the same contract the home
 * page's dark bands read, which the Day palette remaps to white chrome and the
 * mist ladder (\`css/skin-light.css\` is the contract) — then the brand/ink
 * ramp, then the Horizon dark literal, so the chrome holds its voice even on a
 * shop whose palette has not authored them yet.
 *
 * They are declared on the shell elements themselves (not only on \`.ae\`)
 * because the remap rule after this one needs them where the shell's inline
 * styles resolve. \`header:has(+ div > .ae-departments)\` keeps the header and
 * the band one piece of chrome even on a shop that switched the search off. */
header:has(.ae-search),
header:has(+ div > .ae-departments),
div:has(> .ae-departments) > .sf-vat-strip,
.ae-departments{
  --ae-chrome:var(--color-band-chrome,var(--color-brand-900,#0a1424));
  --ae-chrome-field:var(--color-band-raised,var(--color-brand-800,#11203a));
  --ae-chrome-hover:var(--color-band-hover,var(--color-brand-700,#1b2d4a));
  --ae-chrome-ink:var(--color-text-inverse,#e8eef7);
  --ae-chrome-muted:var(--color-text-inverse-muted,#9fb0c8);
  --ae-chrome-line:var(--color-border-inverse,rgba(232,238,247,.12));
  --ae-chrome-line-strong:var(--color-border-inverse-strong,rgba(232,238,247,.28));
  --ae-chrome-accent:var(--color-accent-on-band,var(--color-accent-500,#ffb547));
  --ae-chrome-on-accent:var(--color-text-on-accent,#10233e);
  --ae-chrome-focus:var(--ae-chrome-accent);
}

/* ── The chrome bridge ──────────────────────────────────────────────────────
 * The shell's header and the VAT strip's switch are runtime markup styled by
 * INLINE styles this theme does not own — but every colour in them is read
 * through a \`var(--color-*)\`, so redefining those custom properties on the
 * element recolours the inline styles through the cascade's front door: no
 * \`!important\`, no specificity fight, no geometry touched (the 25 September
 * header breakage was a LAYOUT fight; colour tokens cannot move a box). The
 * header element, its text, its hairline, the basket badge and the VAT
 * switch's pressed pill all flip to the ink voice in one place.
 *
 * Only colour-role names are remapped, never the geometry tokens
 * (\`--radius-*\`, \`--shadow-*\`), which the skin must leave to the platform.
 * Everything these read resolves from the band/ink tokens above — nothing
 * here feeds a remapped name back into itself. */
header:has(.ae-search),
header:has(+ div > .ae-departments),
div:has(> .ae-departments) > .sf-vat-strip{
  --color-surface-card:var(--ae-chrome);
  --color-surface-muted:var(--ae-chrome-field);
  --color-text-primary:var(--ae-chrome-ink);
  --color-text-secondary:var(--ae-chrome-muted);
  --color-neutral-100:var(--ae-chrome-hover);
  --color-neutral-200:var(--ae-chrome-line);
  --color-border-subtle:var(--ae-chrome-line);
  --color-border-strong:var(--ae-chrome-line-strong);
  --color-border-focus:var(--ae-chrome-focus);
  --color-brand-500:var(--ae-chrome-accent);
  --color-brand-600:var(--ae-chrome-accent);
  --color-text-on-brand:var(--ae-chrome-on-accent);
}

.ae *:focus-visible{outline:2px solid var(--ae-focus);outline-offset:2px}
`;
